<script setup>
import { ref, computed } from 'vue';
import AppMenuItem from './AppMenuItem.vue';
import { useDynamicPermissions } from '@/composables/useDynamicPermissions'
import { useModuleAccess, ModuleCode } from '../composables/useModuleAccess';
import * as PermissionType from '../enums/PermissionType';
import { useStates } from '../router/states';

const { canShowMenuItem } = useDynamicPermissions();
const { hasModuleAccess } = useModuleAccess();
const { isWithholdingAgent } = useStates();

// Definición base del menú con configuraciones de permisos
const baseMenuModel = [
  {
    label: 'section.initial',
    icon: 'pi pi-building',
    items: [
      {
        label: 'Dashboard',
        icon: 'pi pi-chart-line',
        to: '/',
      },
      {
        label: 'shared.sales',
        icon: 'pi pi-money-bill',
        moduleCode: ModuleCode.SALES,
        permission: {
          moduleType: 'sales',
          permissionFlag: PermissionType.Sales.READ,
        },
        items: [
          {
            label: 'clients.titleMenu',
            icon: 'pi pi-users',
            to: '/clients',
            permission: {
              moduleType: 'sales',
              permissionFlag: PermissionType.Sales.READ,
            },
          },
          {
            label: 'sales.invoices',
            icon: 'fas fa-file-invoice',
            to: '/sales-invoices',
            permission: {
              moduleType: 'sales',
              permissionFlag: PermissionType.Sales.READ,
            },
          },
          {
            label: 'sales.salesNotes',
            icon: 'fas fa-receipt',
            to: '/sales-notes',
            permission: {
              moduleType: 'sales',
              permissionFlag: PermissionType.Sales.READ,
            },
          },
          {
            label: 'sales.proformas',
            icon: 'fas fa-file-contract',
            to: '/proformas',
            permission: {
              moduleType: 'sales',
              permissionFlag: PermissionType.Sales.READ,
            },
          },
          {
            label: 'sales.creditNotes',
            icon: 'fas fa-file-invoice-dollar',
            to: '/credit-notes',
            permission: {
              moduleType: 'sales',
              permissionFlag: PermissionType.Sales.READ,
            },
          },
          {
            label: 'sales.debitNotes',
            icon: 'fas fa-file-medical-alt',
            to: '/debit-notes',
            permission: {
              moduleType: 'sales',
              permissionFlag: PermissionType.Sales.READ,
            },
          },
          {
            label: 'receivedWithholdings.title',
            icon: 'fas fa-hand-holding-usd',
            to: '/received-withholdings',
            permission: {
              moduleType: 'sales',
              permissionFlag: PermissionType.Sales.READ,
            },
          },
        ],
      },
      {
        label: 'shared.purchases',
        icon: 'pi pi-shopping-bag',
        moduleCode: ModuleCode.PURCHASES,
        permission: {
          moduleType: 'purchases',
          permissionFlag: PermissionType.Purchases.READ,
        },
        items: [
          {
            label: 'suppliers.titleMenu',
            icon: 'pi pi-briefcase',
            to: '/suppliers',
            permission: {
              moduleType: 'purchases',
              permissionFlag: PermissionType.Purchases.READ,
            },
          },
          {
            label: 'withholdingElectronics.title',
            icon: 'fas fa-file-invoice',
            to: '/withholding-electronics',
            permission: {
              moduleType: 'purchases',
              permissionFlag: PermissionType.Purchases.READ,
            },
            requiresWithholdingAgent: true,
          },
          {
            label: 'purchases.invoices',
            icon: 'pi pi-file-edit',
            to: '/purchases-invoices',
            permission: {
              moduleType: 'purchases',
              permissionFlag: PermissionType.Purchases.READ,
            },
          },
          {
            label: 'billing.purchaseCreditNoteTitle',
            icon: 'fas fa-file-invoice-dollar',
            to: '/purchases-credit-notes',
            permission: {
              moduleType: 'purchases',
              permissionFlag: PermissionType.Purchases.READ,
            },
          },
          {
            label: 'billing.purchaseDebitNoteTitle',
            icon: 'fas fa-file-medical-alt',
            to: '/purchases-debit-notes',
            permission: {
              moduleType: 'purchases',
              permissionFlag: PermissionType.Purchases.READ,
            },
          },
          {
            label: 'purchases.purchasesNotes',
            icon: 'fas fa-clipboard-list',
            to: '/purchases-notes',
            permission: {
              moduleType: 'purchases',
              permissionFlag: PermissionType.Purchases.READ,
            },
          },
        ],
      },
      {
        label: 'accounting.menuTitle',
        icon: 'pi pi-calculator',
        moduleCode: ModuleCode.ACCOUNTING,
        permission: {
          moduleType: 'accounting',
          permissionFlag: PermissionType.Accounting.READ,
        },
        items: [
          {
            label: 'accounting.chartOfAccounts',
            icon: 'pi pi-list',
            to: '/accounting/chart-of-accounts',
            permission: {
              moduleType: 'accounting',
              permissionFlag: PermissionType.Accounting.READ,
            },
          },
          {
            label: 'accounting.productCategories',
            icon: 'pi pi-tags',
            to: '/accounting/product-categories',
            permission: {
              moduleType: 'accounting',
              permissionFlag: PermissionType.Accounting.READ,
            },
          },
          {
            label: 'accounting.journalEntries',
            icon: 'pi pi-book',
            to: '/accounting/journal-entries',
            permission: {
              moduleType: 'accounting',
              permissionFlag: PermissionType.Accounting.READ,
            },
          },
          {
            label: 'accounting.journal',
            icon: 'pi pi-file',
            to: '/accounting/journal',
            permission: {
              moduleType: 'accounting',
              permissionFlag: PermissionType.Accounting.READ,
            },
          },
          {
            label: 'accounting.generalLedger',
            icon: 'pi pi-table',
            to: '/accounting/general-ledger',
            permission: {
              moduleType: 'accounting',
              permissionFlag: PermissionType.Accounting.READ,
            },
          },
          {
            label: 'accounting.trialBalance',
            icon: 'pi pi-chart-bar',
            to: '/accounting/trial-balance',
            permission: {
              moduleType: 'accounting',
              permissionFlag: PermissionType.Accounting.READ,
            },
          },
          {
            label: 'accounting.financialStatements',
            icon: 'pi pi-file-pdf',
            to: '/accounting/financial-statements',
            permission: {
              moduleType: 'accounting',
              permissionFlag: PermissionType.Accounting.READ,
            },
          },
        ],
      },
      {
        label: 'waybillRemissions.menuTitle',
        icon: 'fas fa-truck',
        permission: {
          moduleType: 'sales',
          permissionFlag: PermissionType.Sales.READ,
        },
        items: [
          {
            label: 'carriers.title',
            icon: 'fas fa-id-card',
            to: '/waybill-remissions/carriers',
            permission: {
              moduleType: 'sales',
              permissionFlag: PermissionType.Sales.READ,
            },
          },
          {
            label: 'waybillRemissions.emitted',
            icon: 'fas fa-truck',
            to: '/waybill-remissions/emitted',
            permission: {
              moduleType: 'sales',
              permissionFlag: PermissionType.Sales.READ,
            },
          },
        ],
      },
      {
        label: 'shared.inventory',
        icon: 'fas fa-tasks',
        to: '/products',
        moduleCode: ModuleCode.INVENTORY,
        permission: {
          moduleType: 'inventory',
          permissionFlag: PermissionType.Inventory.READ,
        },
      },
      {
        label: 'expenses.titleMenu',
        icon: 'fas fa-receipt',
        to: '/expenses',
        permission: {
          moduleType: 'inventory',
          permissionFlag: PermissionType.Inventory.READ,
        },
      },
      {
        label: 'internalInventory.titleMenu',
        icon: 'fas fa-warehouse',
        to: '/internal-inventory',
        permission: {
          moduleType: 'inventory',
          permissionFlag: PermissionType.Inventory.READ,
        },
      },
      {
        label: 'ats.titleMenu',
        icon: 'fas fa-file-code',
        to: '/ats',
        permission: {
          moduleType: 'sales',
          permissionFlag: PermissionType.Sales.READ,
        },
      },
      {
        label: 'companySettings.titleMenu',
        icon: 'pi pi-fw pi-cog',
        to: '/companysettings',
        moduleCode: ModuleCode.CONFIGURATION,
        permission: {
          moduleType: 'configuration',
          permissionFlag: PermissionType.Configuration.READ,
        },
      },
      {
        label: 'permissions.management',
        icon: 'pi pi-fw pi-shield',
        to: '/settings/permissions',
        moduleCode: ModuleCode.CONFIGURATION,
        permission: {
          moduleType: 'configuration',
          permissionFlag: PermissionType.Configuration.ALL,
        },
      },
    ],
  },
];

// Función para filtrar elementos del menú basado en permisos y módulos
// IMPORTANTE: Es NO MUTATIVA - devuelve nuevos objetos para que el computed
// pueda re-evaluar correctamente cuando cambien flags reactivos como isWithholdingAgent.
const filterMenuItems = (items) => {
  const result = [];
  for (const original of items) {
    // Bloquear items que requieren ser Agente de Retención si la compañía no lo es
    if (original.requiresWithholdingAgent && !isWithholdingAgent.value) {
      continue;
    }

    // NUEVO: Validar acceso al módulo (suscripción)
    // Módulos del sistema siempre disponibles
    const systemModules = ['GLOBAL', 'CONFIGURATION', 'USERS'];
    if (original.moduleCode && !systemModules.includes(original.moduleCode) && !hasModuleAccess(original.moduleCode)) {
      continue;
    }

    // Si tiene permisos definidos y NO los cumple, descartar
    if (original.permission) {
      const hasPermission = canShowMenuItem(original.permission);
      if (!hasPermission.value) continue;
    }

    // Si tiene subelementos, filtrar recursivamente sin mutar el original
    if (original.items) {
      const filteredChildren = filterMenuItems(original.items);
      if (filteredChildren.length === 0) continue;
      result.push({ ...original, items: filteredChildren });
    } else {
      result.push({ ...original });
    }
  }
  return result;
};

// Menú reactivo que se actualiza según los permisos del usuario y flags globales
const model = computed(() => {
  return baseMenuModel
    .map((section) => ({
      ...section,
      items: filterMenuItems(section.items),
    }))
    .filter((section) => section.items.length > 0);
});
</script>

<template>
  <ul class="layout-menu">
    <template v-for="(item, i) in model" :key="item">
      <AppMenuItem :item="item" root :index="i" />

      <li class="menu-separator"></li>
    </template>
  </ul>
</template>
